import { useTranslation } from 'react-i18next';
import { DarwinButton, DarwinFlex, DarwinListView } from 'components/darwin-ui';
import useTableControls from 'hooks/useTableControls';
import { useGetOutputCellById } from 'api/services/processMapService';
import Icon from 'components/darwin-ui/Icon';
import DarwinTypography from 'components/darwin-ui/Typography';
import styles from './ProcessMap.module.css';
import { useUI } from 'contexts/UiContext';
import { TFunction } from 'i18next';
import { DTableProps, formatDate } from '@darwin-ui';
import { OutPutCellReport } from 'types/processBlockTypes';
import { translateFormSelect } from 'utils/common';

const { Text } = DarwinTypography;

export const getOutputCellColumns = (t: TFunction) => {
  const columns: DTableProps<OutPutCellReport>['columns'] = [
    {
      title: t('reports.columns.transaction-id'),
      dataIndex: 'transactionId',
      key: 'transactionId',
      sorter: true,
      render: transactionId => <span>{transactionId}</span>,
    },
    {
      title: t('reports.columns.date'),
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      sorter: true,
      render: transactionDate => (
        <span>{formatDate(transactionDate, 'dd/MM/yyyy')}</span>
      ),
    },
    {
      title: t('reports.columns.product'),
      dataIndex: 'product',
      key: 'product',
      sorter: true,
      render: product => <span>{product}</span>,
    },
    {
      title: t('reports.columns.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: true,
      render: quantity => <span>{quantity}</span>,
    },
    {
      title: t('reports.columns.unit-of-measure'),
      dataIndex: 'unitOfMeasure',
      key: 'unitOfMeasure',
      sorter: true,
      render: unitOfMeasure => (
        <span>{translateFormSelect(t, unitOfMeasure)}</span>
      ),
    },
    {
      title: t('reports.columns.balance'),
      dataIndex: 'balance',
      key: 'balance',
      sorter: true,
      render: balance => <span>{balance}</span>,
    },
    {
      title: t('reports.columns.ref-doc-number'),
      dataIndex: 'referenceDocumentNumber',
      key: 'referenceDocumentNumber',
      sorter: true,
      render: referenceDocumentNumber => <span>{referenceDocumentNumber}</span>,
    },
    {
      title: t('reports.columns.user'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      sorter: true,
      render: user => <span>{user}</span>,
    },
  ];

  return columns;
};

type OutputCellModalTableProps = {
  planId: string;
  outputCellId: string | number;
  connectionId: string;
  sourceBlockName: string;
  targetBlockName: string;
  canDisconnect: boolean;
  onDisconnect: (connectionId: string, processName: string) => void;
};

export const OutputCellModalTable = ({
  planId,
  outputCellId,
  connectionId,
  sourceBlockName,
  targetBlockName,
  canDisconnect,
  onDisconnect,
}: OutputCellModalTableProps) => {
  const { t } = useTranslation();
  const { selectedCompany } = useUI();
  const {
    pageSize,
    currentPage,
    onPageSizeChange,
    setCurrentPage,
    sortOrder,
    onTableChange,
  } = useTableControls({ defaultSortBy: 'transactionDate' });

  // Each table manages its own data fetching with independent pagination/sorting
  const {
    data: outputCellReportsData,
    isLoading: isLoadingOutputCellReports,
    isFetching: isFetchingOutputCellReports,
    isPending: isPendingOutputCellReports,
  } = useGetOutputCellById({
    companyId: selectedCompany || '',
    planId,
    outputCellId,
    page: currentPage,
    limit: pageSize,
    sortOrder,
    connectionId,
  });

  const transactionGroup = outputCellReportsData?.data?.transactionGroups?.[0];
  const transactions = transactionGroup?.transactions ?? [];
  const total = transactionGroup?.total ?? 0;

  const isLoading =
    isLoadingOutputCellReports ||
    isFetchingOutputCellReports ||
    isPendingOutputCellReports;

  const columns = getOutputCellColumns(t);

  return (
    <DarwinListView<OutPutCellReport>
      header={{
        title: (
          <DarwinFlex
            gap={12}
            justify="center"
            style={{ alignItems: 'center' }}
          >
            <Text>{sourceBlockName}</Text>
            <Icon name="titleSeparator" size="xxxl"></Icon>
            <Text>{targetBlockName}</Text>
          </DarwinFlex>
        ),
        searchPlaceholder: t('reports.search-placeholder'),
        actions: (
          <DarwinButton
            type="default"
            danger
            onClick={() => {
              onDisconnect(
                connectionId,
                sourceBlockName + ' -> ' + targetBlockName
              );
            }}
            disabled={!canDisconnect}
          >
            <Text className={styles.outputCellDisconnect}>
              {t('outputcell.disconnect')}{' '}
            </Text>
            <Icon name="disconnectOutputCell" size="xs"></Icon>
          </DarwinButton>
        ),
      }}
      data={transactions}
      columns={columns}
      isLoading={isLoading}
      pagination={{
        total: total,
        pageSize,
        currentPage,
        onPageChange: setCurrentPage,
        onPageSizeChange,
      }}
      table={{
        onChange: onTableChange,
      }}
      emptyText={t('common-table.empty-search')}
    />
  );
};
